import { Copy } from "lucide-react";
import ad5 from "@/assets/fb-ad-5-depends-on-me.jpg";
import ad6 from "@/assets/fb-ad-6-competitor.jpg";
import ad7 from "@/assets/fb-ad-7-second-location.jpg";
import ad8 from "@/assets/fb-ad-8-family-pressure.jpg";
import ad9 from "@/assets/fb-ad-9-ppo-trap.jpg";
import ad10 from "@/assets/fb-ad-10-exit-strategy.jpg";

const angleMap = [
  { id: "#5", angle: "It All Depends on Me", emotion: "Fear of fragility", priority: "Week 1" },
  { id: "#6", angle: "Competitor Is Winning", emotion: "Competitive anxiety", priority: "Week 1" },
  { id: "#7", angle: "Second Location", emotion: "Ambition / uncertainty", priority: "Week 3" },
  { id: "#8", angle: "Family Pressure", emotion: "Personal stakes", priority: "Week 2" },
  { id: "#9", angle: "PPO Trap", emotion: "Financial identity", priority: "Week 2" },
  { id: "#10", angle: "Exit Strategy", emotion: "Legacy / retirement", priority: "Week 3" },
];

const ads = [
  {
    id: 5,
    angle: `"It All Depends on Me" Angle`,
    recommended: true,
    startHere: true,
    image: ad5,
    primary: `What happens to your practice if you take 2 weeks off?\n\nIf the answer is "production drops, new patients slow down, and nobody follows up properly"… you don't have a growth problem. You have a systems problem.\n\nAt $1M+, most owners are still the engine. The marketing runs because you push it. The phones get answered because you remind the team. New patients show up because of your reputation — not because of a machine that works without you.\n\nWe built a No-Touch Practice Growth Analysis specifically for this stage. It scores your practice across 6 areas — marketing, tracking, phones, scheduling, case acceptance, and retention — and shows you exactly where the bottleneck is.\n\nNo call. No pitch. Answer a few questions and get your Growth Score plus a 90-day plan to build a practice that grows whether you're there or not.`,
    headline: "Is Your Practice Growing — Or Just Surviving on You?",
    description: "Free Growth Score for $1M+ practices. No call required.",
    button: "Learn More",
  },
  {
    id: 6,
    angle: `"Competitor Is Winning" Angle`,
    recommended: true,
    startHere: true,
    image: ad6,
    primary: `The practice 3 miles from you just added 40 new patients last month.\n\nThey're not smarter than you. They're not a better dentist. They just know their numbers — and you don't.\n\nHere's what we see when we analyze $1M+ practices: the ones growing fastest aren't spending the most on marketing. They're spending the smartest. They know exactly how many leads come in, how many schedule, how many show up, and what each one is worth.\n\nThe ones stuck? They're guessing.\n\nOur No-Touch Practice Growth Analysis scores your practice against hundreds of offices in your revenue range. You'll see exactly where you're ahead, where you're behind, and what to fix first.\n\nTakes 3 minutes. No call. No obligation. Just your score and a 90-day plan.`,
    headline: "How Does Your Practice Stack Up?",
    description: "Get your Growth Score and see where you rank vs. similar offices.",
    button: "Learn More",
  },
  {
    id: 7,
    angle: `"Second Location" Angle`,
    recommended: false,
    image: ad7,
    primary: `Thinking about opening a second location? Don't — until you see this.\n\nWe've worked with dozens of practices that expanded too early. The first location was doing "$1M+" on paper, but underneath: marketing was inefficient, 30% of leads never got called back, and collections were flat because case acceptance was leaking.\n\nThey didn't need a second location. They needed to fix the first one.\n\nOur No-Touch Practice Growth Analysis was built for exactly this moment. Before you sign a lease, hire more staff, or take on debt — get a clear picture of whether your current practice is actually optimized.\n\nWe'll score you across 6 categories and give you a 90-day roadmap. If Location 1 is dialed in, you'll know you're ready. If it's not, you'll know exactly what to fix before you scale.\n\n3 minutes. No call. No sales pitch.`,
    headline: "Before You Expand, Know Your Real Numbers",
    description: "Practice Growth Score for $1M+ offices considering growth.",
    button: "Learn More",
  },
  {
    id: 8,
    angle: `"Family Pressure" Angle`,
    recommended: true,
    image: ad8,
    primary: `Your spouse doesn't care about your new-patient numbers.\n\nThey care that you're home for dinner. That you're not checking your phone during your kid's game. That the stress of running a $1M+ practice isn't following you into every room of the house.\n\nHere's the thing: the stress isn't because the practice is too big. It's because the systems aren't built to run without constant attention. You're managing marketing, watching the schedule, chasing production — and none of it is automated.\n\nWe built a No-Touch Practice Growth Analysis for owners at this exact stage. It identifies the 2-3 things in your practice that are creating the most drag — and gives you a 90-day plan to fix them.\n\nNot so you can "grow faster." So you can grow smarter — and get some of your life back.\n\nNo call. No pitch. Just your score and a plan.`,
    headline: "Build a Practice That Doesn't Require All of You",
    description: "Growth Score + 90-day plan for $1M+ dentists who want their life back.",
    button: "Learn More",
  },
  {
    id: 9,
    angle: `"PPO Trap" Angle`,
    recommended: true,
    image: ad9,
    primary: `Still collecting 60 cents on the dollar from PPOs?\n\nEvery $1M+ practice owner we talk to has the same thought: "I want to drop insurance plans, but I'm afraid I'll lose patients."\n\nThat fear is valid — if you don't know your numbers. But when you can see exactly how many new patients come from PPOs vs. direct marketing, how many would stay at full fee, and where your real production is coming from… the decision becomes math, not fear.\n\nOur No-Touch Practice Growth Analysis breaks this down for you. We score your practice across 6 areas — including how dependent your patient flow is on insurance directories vs. your own marketing engine.\n\nIf you're ready to drop PPOs, your score will confirm it. If you're not ready yet, you'll see exactly what to build first so you can make the move with confidence.\n\n3 minutes. No call. No obligation.`,
    headline: "Your Growth Score Shows If You Can Drop PPOs — Safely",
    description: "See exactly where your new-patient flow would hold without insurance dependency.",
    button: "Learn More",
  },
  {
    id: 10,
    angle: `"Exit Strategy" Angle`,
    recommended: false,
    image: ad10,
    primary: `Planning to sell your practice in the next 3-5 years?\n\nHere's what buyers and DSOs look at before they make an offer: predictable new-patient flow, documented systems, clean tracking from marketing spend to collections, and a practice that doesn't depend entirely on the owner.\n\nMost $1M+ practices we analyze are missing at least 2 of those 4. That's not a dealbreaker — but it is a valuation discount. Sometimes a big one.\n\nOur No-Touch Practice Growth Analysis scores your practice across the exact categories that drive acquisition value. You'll see where you're strong, where a buyer would push back, and what to fix in the next 90 days to maximize your number.\n\nWhether you're selling next year or in five years, the time to optimize is now — not 6 months before you list.\n\n3 minutes. No call. No pitch. Just your score and a plan.`,
    headline: "What's Your Practice Actually Worth to a Buyer?",
    description: "Growth Score reveals the gaps that lower your valuation.",
    button: "Learn More",
  },
];

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

const priorityColor = (p: string) => {
  if (p === "Week 1") return "bg-emerald-500/20 text-emerald-400";
  if (p === "Week 2") return "bg-amber-500/20 text-amber-400";
  return "bg-blue-500/20 text-blue-400";
};

const FbAdsV2Page = () => {
  return (
    <div className="min-h-screen bg-hero-bg text-hero-foreground">
      {/* Header */}
      <div className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-2">
            Facebook Ad Creatives — Expansion Pack
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Practice Growth Analysis — 6 New Angles
          </h1>
          <p className="text-hero-foreground/60 max-w-2xl leading-relaxed">
            6 new angles targeting $1M+ dental practice owners. Each ad drives to the{" "}
            <a href="/apply" className="text-accent hover:underline">
              Growth Analysis landing page
            </a>
            . Start with #5 or #6, use Dr. Patel video as creative, then iterate off the winner.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-16">
        {/* Setup Notes */}
        <div className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6 mb-10">
          <h2 className="font-bold mb-3">FB Ads Manager Setup</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-hero-foreground/60">
            <div>
              <p className="font-medium text-hero-foreground mb-1">Campaign Objective</p>
              <p>Conversions → Website Conversions (optimize for form submit)</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Targeting</p>
              <p>Dentists, Dental Practice Owners, $1M+ revenue interests, 35-65 age</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Destination URL</p>
              <p className="font-mono text-xs break-all">
                {window.location.origin}/apply?utm_source=facebook&utm_medium=paid&utm_campaign=growth-analysis
              </p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Placements</p>
              <p>Facebook Feed, Instagram Feed, Facebook Stories (start manual, then expand)</p>
            </div>
          </div>
        </div>

        {/* Angle Map Table */}
        <div className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6 mb-10">
          <h2 className="font-bold mb-4">Angle Map — What Each Ad Targets</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-hero-foreground/50 text-xs uppercase tracking-wider">
                  <th className="text-left pb-3 pr-4">Ad</th>
                  <th className="text-left pb-3 pr-4">Angle</th>
                  <th className="text-left pb-3 pr-4">Core Emotion</th>
                  <th className="text-left pb-3">Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hero-foreground/10">
                {angleMap.map((row) => (
                  <tr key={row.id}>
                    <td className="py-3 pr-4 font-bold">{row.id}</td>
                    <td className="py-3 pr-4">{row.angle}</td>
                    <td className="py-3 pr-4 text-hero-foreground/70">{row.emotion}</td>
                    <td className="py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${priorityColor(row.priority)}`}>
                        {row.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ad Cards */}
        <div className="space-y-12">
          {ads.map((ad) => (
            <div
              key={ad.id}
              className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 overflow-hidden"
            >
              {/* Ad Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-hero-foreground/10">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-bold">
                    {ad.id}
                  </span>
                  <h3 className="font-bold">{ad.angle}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {ad.recommended && (
                    <span className="bg-amber-500/15 text-amber-400 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Recommended
                    </span>
                  )}
                  {ad.startHere && (
                    <span className="bg-emerald-500/15 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Start Here
                    </span>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2">
                {/* Creative Preview */}
                <div className="p-6 flex items-center justify-center">
                  <div className="w-full max-w-sm">
                    <div className="bg-hero-foreground/10 rounded-lg border border-hero-foreground/10 overflow-hidden">
                      <div className="p-3 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-accent-foreground text-xs font-bold">
                          XP
                        </div>
                        <div>
                          <p className="text-xs font-semibold">XPress Promotion</p>
                          <p className="text-[10px] text-hero-foreground/50">Sponsored · 🌐</p>
                        </div>
                      </div>
                      <p className="px-3 pb-2 text-xs text-hero-foreground/80 line-clamp-3">
                        {ad.primary.split("\n")[0]}
                      </p>
                      <img
                        src={ad.image}
                        alt={ad.angle}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                        width={1080}
                        height={1080}
                      />
                      <div className="p-3 border-t border-hero-foreground/10">
                        <p className="text-[10px] text-hero-foreground/50 uppercase">
                          {window.location.host}
                        </p>
                        <p className="text-xs font-semibold truncate">{ad.headline}</p>
                        <p className="text-[10px] text-hero-foreground/50 truncate">{ad.description}</p>
                      </div>
                      <div className="px-3 pb-3">
                        <div className="bg-hero-foreground/10 text-hero-foreground text-xs font-semibold text-center py-2 rounded">
                          {ad.button}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Copy */}
                <div className="p-6 space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50">
                        Primary Text
                      </label>
                      <button
                        onClick={() => copyToClipboard(ad.primary)}
                        className="text-xs text-accent hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <div className="bg-hero-foreground/5 rounded-lg p-4 text-sm whitespace-pre-line leading-relaxed max-h-60 overflow-y-auto border border-hero-foreground/10">
                      {ad.primary}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50 block mb-1">
                      Headline
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold flex-1">{ad.headline}</p>
                      <button
                        onClick={() => copyToClipboard(ad.headline)}
                        className="text-hero-foreground/40 hover:text-accent"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50 block mb-1">
                      Description
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-hero-foreground/70 flex-1">{ad.description}</p>
                      <button
                        onClick={() => copyToClipboard(ad.description)}
                        className="text-hero-foreground/40 hover:text-accent"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50 block mb-1">
                      CTA Button
                    </label>
                    <p className="text-sm font-medium">{ad.button}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Launch Playbook */}
        <div className="mt-12 bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6">
          <h3 className="font-bold mb-5">Launch Playbook</h3>
          <div className="space-y-4">
            {[
              {
                step: 1,
                title: "Start with Ad #5 and #6",
                desc: "These have the broadest appeal and strongest emotional urgency for $1M+ owners. Run both simultaneously to test which resonates more.",
              },
              {
                step: 2,
                title: "Use Dr. Patel video as creative for #5, #6, #9",
                desc: "Swap the static image for the testimonial video for higher engagement. For #7, #8, #10 — consider filming a new Hans video addressing each specific life-stage topic.",
              },
              {
                step: 3,
                title: "Run at $30–50/day per ad for 5–7 days",
                desc: "Don't judge performance before the learning phase completes. Optimize for form completions, not link clicks.",
              },
              {
                step: 4,
                title: "Layer in #8 and #9 in Week 2",
                desc: 'The "Family Pressure" and "PPO Trap" angles target deeper emotional triggers. They may have lower CTR but higher-quality leads.',
              },
              {
                step: 5,
                title: "Add #7 and #10 in Week 3",
                desc: "Narrower audiences (expansion-minded and exit-planning dentists) but extremely high-value leads when they convert.",
              },
              {
                step: 6,
                title: "Track with UTM parameters in GHL",
                desc: "Each ad has a unique utm_content tag. Use these to track which angle drives the highest-quality leads — not just the most clicks.",
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-4">
                <span className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  {item.step}
                </span>
                <div>
                  <p className="font-semibold text-sm">{item.title}</p>
                  <p className="text-sm text-hero-foreground/60 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FbAdsV2Page;
